
import Cookies from 'js-cookie';
import modals from './modal';
import analytics from './analytics';

const cookiePopup = (() => {
  const COOKIE = 'cookie_consent';

  /**
   * Pushes objects to datalayer for GTM
   *
   * @param {object} data - Datalayer data
   */
  function triggerGtm(data = {}) {
    window.dataLayer = window.dataLayer || [];

    Object.keys(data).forEach(key => {
      let dlData;

      if(key === 'cookies_all') {
        // "All" is special, needs cookie categories explicitly
        dlData = {
          event: key,
          [key + '_accepted']: data[key],
          cookies_ux_accepted: true,
          cookies_ad_accepted: true
        };
      } else if(data[key]) {
        dlData = {
          event: key,
          [key + '_accepted']: data[key]
        };
      } else {
        dlData = {
          [key + '_accepted']: data[key]
        };
      }

      if(dlData) {
        window.dataLayer.push(dlData);
      }
    });

    analytics.triggerDelayedDataLayer();
  }

  /**
   * Gets form field values as key-value pairs
   *
   * @param {HTMLElement} form - Form element
   *
   * @return {object}
   */
  function serialize(form) {
    const serialized = {};

    if(form) {
      form.elements.forEach(field => {
        if(field.name) {
          if((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
            serialized[field.name] = field.value;
          }
        }
      });
    }

    return serialized;
  }

  /**
   * Formats form data for GTM
   *
   * @param {object} data - Form data as key-value pairs
   *
   * @return {object} formattedData
   */
  function formatFormData(data) {
    const formattedData = {...data};

    // Format to booleans
    Object.keys(formattedData).forEach(key => {
      formattedData[key] = formattedData[key] === '1';
    });

    return formattedData;
  }

  /**
   * Handles cookie form submit event
   * Triggers GTM, closes modal, hides notice
   *
   * @param {Event} event - Submit event
   */
  function onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const formData = formatFormData(serialize(form));

    // Send values to tag manager
    triggerGtm(formData);

    // Save values to cookie
    setCookie(formData);

    // Close cookie popup, if open
    closePopup();

    // Also hide cookie notice
    hideCookieNotice();
  }

  /**
   * Approves all cookies,
   * triggers GTM event and saves settings to cookie,
   * closes modal (if open),
   * and hides cookie notice
   *
   * @param {HTMLElement} triggerBtn - "Approve all" button
   */
  function approveAll(triggerBtn) {
    const cookieValue = {cookies_all: true};

    // Send value to GTM
    triggerGtm(cookieValue);

    // Save value to cookie
    setCookie(cookieValue);

    // Update modal form values
    updateModalContents(cookieValue);

    // Close cookie popup, if open
    closePopup();

    // Also hide cookie notice
    hideCookieNotice();
  }

  /**
   * Denies all extra cookies
   * (only mandatory accepted)
   */
  function denyAll() {
    const cookieValue = {cookies_ux: false, cookies_ad: false};

    // Send value to GTM
    triggerGtm(cookieValue);

    // Save value to cookie
    setCookie(cookieValue);

    // Update modal form values
    updateModalContents(cookieValue);

    // Also hide cookie notice
    hideCookieNotice();
  }

  /**
   * Saves cookie consent data to a cookie
   *
   * @param {object} data - Cookie value (will be stringified)
   */
  function setCookie(data) {
    Cookies.set(COOKIE, JSON.stringify(data), { expires: 365 });
  }

  /**
   * Remove existing consent cookie
   */
  function removeCookie() {
    Cookies.remove(COOKIE);
  }

  /**
   * Closes cookie popup modal
   */
  function closePopup() {
    modals.close();
  }

  /**
   * Hides cookie notice ribbon
   */
  function hideCookieNotice() {
    const notice = document.getElementById('cookie-notice');
    if(notice) {
      notice.style.display = 'none';
    }
  }

  /**
   * Shows cookie notice ribbon
   */
  function showCookieNotice() {
    const notice = document.getElementById('cookie-notice');
    if(notice) {
      notice.style.display = 'block';
      notice.querySelector('button')?.focus();
    }
  }

  /**
   * Updates cookie consent modal form values based on cookieSettings
   *
   * @param {object} cookieSettings - Consent for each cookie type as object
   */
  function updateModalContents(cookieSettings) {
    const form = document.querySelector('.js-cookie-popup-form');

    if(form) {
      Object.keys(cookieSettings).forEach(cookieType => {

        if(cookieType === 'cookies_all') {
          const inputs = form.querySelectorAll('input[type="radio"][value="1"]');
          if(inputs) {
            inputs.forEach(input => input.checked = true);
          }
        } else {
          const accepted = cookieSettings[cookieType];
          // Radio buttons, one for yes and one for no
          const input = form.querySelector(`[name=${cookieType}][value="${accepted ? '1' : '0'}"]`);
          if(input) {
            input.checked = true;
          }
        }
      });
    }
  }

  /**
   * Checks whether cookie consent has been given previously
   * and fires GTM immediately if consent found.
   */
  function checkExistingConsent() {
    const consentCookie = Cookies.get(COOKIE);

    if(consentCookie) {
      try {
        const cookieSettings = JSON.parse(consentCookie);
        triggerGtm(cookieSettings);
        // Delete element (proxy cache)
        hideCookieNotice();

        // Update consent modal form values
        updateModalContents(cookieSettings);

      } catch(e) {
        // Cannot determine values
        showCookieNotice();
      }
    } else {
      showCookieNotice();
    }
  }

  /**
   * Inits cookie popup functionality.
   */
  function init() {
    // Add event listeners
    document.addEventListener('click', function(event) {
      if(event.target.matches('.js-cookie-popup-approve-all')) {
        approveAll(event.target);
      }
    }, false);

    document.addEventListener('click', function(event) {
      if(event.target.matches('.js-cookie-popup-deny-all')) {
        denyAll();
      }
    }, false);

    document.addEventListener('submit', function(event) {
      if(event.target.matches('.js-cookie-popup-form')) {
        onSubmit(event);
      }
    });

    document.addEventListener('click', function(event) {
      if(event.target.matches('.js-trigger-cookie-notice')) {
        showCookieNotice();
      }
    }, false);


    // Check existing cookie consent settings
    checkExistingConsent();
  }

  return {
    init,
    removeCookie
  };
})();

export default cookiePopup;
